import { Controller } from "@hotwired/stimulus";

// MC-53: theme switch controller. Three-way radio (system / light /
// dark) inside the avatar dropdown. On change, PATCH the endpoint to
// persist, then swap `data-theme` on the document root immediately so
// the user sees the new theme without a reload. When `system` is
// selected, listens to `prefers-color-scheme` so the document tracks
// the OS theme live for the rest of the session.
export default class extends Controller {
  static targets = ["input"];
  static values = { endpoint: String, csrf: String };

  initialize() {
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.onMediaChange = this.onMediaChange.bind(this);
  }

  connect() {
    this.bindMediaListenerIfSystem();
  }

  disconnect() {
    this.unbindMediaListener();
  }

  async change(event) {
    const value = event.target.value;
    document.documentElement.setAttribute("data-theme-pref", value);
    this.applyEffectiveTheme(value);
    this.bindMediaListenerIfSystem(value);
    document.cookie = `theme=${value}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    try {
      await fetch(this.endpointValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfValue
        },
        body: JSON.stringify({ theme: value })
      });
    } catch (_error) {
      // Persistence failure is not fatal — the cookie + DOM swap are
      // already applied for this session. Next request re-syncs from
      // the server-rendered html attribute.
    }
  }

  applyEffectiveTheme(pref) {
    let effective = pref;
    if (pref === "system") {
      effective = this.mediaQuery.matches ? "dark" : "light";
    }
    document.documentElement.setAttribute("data-theme", effective);
  }

  bindMediaListenerIfSystem(pref = this.currentPref()) {
    this.unbindMediaListener();
    if (pref === "system") {
      this.mediaQuery.addEventListener("change", this.onMediaChange);
      this.boundMedia = true;
    }
  }

  unbindMediaListener() {
    if (this.boundMedia) {
      this.mediaQuery.removeEventListener("change", this.onMediaChange);
      this.boundMedia = false;
    }
  }

  onMediaChange() {
    if (this.currentPref() !== "system") return;
    this.applyEffectiveTheme("system");
  }

  currentPref() {
    return document.documentElement.getAttribute("data-theme-pref") || "system";
  }
}
